import * as React from "react";
import { useId, useState } from "react";

import { useScopeClass } from "@/components/ui/scope";
import { cn } from "@/lib/utils";

interface FloatingInputProps extends Omit<React.ComponentProps<"input">, "size"> {
  /** Label rendered inside the field (Reddit-style floating). Omit for an icon-only or
   *  composer-style field with no label. */
  label?: string;
  /** Destructive ring + message under the field. Sets aria-invalid on the input. */
  error?: string;
  /** Hint text rendered under the field. Suppressed if `error` is also set. */
  helperText?: string;
  /** Icon rendered absolutely on the left inside the field. Shifts content right. */
  leadingIcon?: React.ReactNode;
  /** Icon (or button) rendered absolutely on the right inside the field. Pass an
   *  interactive element (button) for click handling — pointer events are not
   *  blocked here. Shifts the input's right padding so text doesn't collide. */
  trailingIcon?: React.ReactNode;
  /** Show the live X/Y character counter when `maxLength` is set. Defaults to true.
   *  Set `false` for short hard-limit fields (year, zip, etc.) where the counter is noise. */
  counter?: boolean;
  /** Extra classes applied to the inner field (label/input). Use for visual tweaks
   *  like extra right padding for an absolute trailing icon. Layout classes
   *  (flex-1, w-X) belong on `className` instead so they affect the outer wrapper. */
  inputClassName?: string;
}

function FloatingInput({
  ref,
  id,
  label,
  error,
  helperText,
  leadingIcon,
  trailingIcon,
  className,
  inputClassName,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  maxLength,
  counter,
  placeholder,
  "aria-describedby": userDescribedBy,
  ...rest
}: FloatingInputProps) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const [focused, setFocused] = useState(false);
  // Controlled: derive the length from `value` — no state, so a keystroke is one parent
  // update, not three. Fast synthetic typing (a browser extension driving the field) tripped
  // React's nested-update limit when every character also set local state (BDAY-484).
  // Uncontrolled: track it locally, since nothing else re-renders us.
  const controlled = value !== undefined;
  const [uncontrolledLength, setUncontrolledLength] = useState(
    () => String(defaultValue ?? "").length,
  );
  const valueLength = controlled ? String(value).length : uncontrolledLength;

  const hasLabel = !!label;
  const hasPlaceholder = !!placeholder;
  // Date/time inputs always render a native internal placeholder
  // (mm/dd/yyyy, --:--), so the label has to float above to avoid colliding.
  const type = rest.type;
  const hasNativePlaceholder =
    type === "date" ||
    type === "datetime-local" ||
    type === "time" ||
    type === "month" ||
    type === "week";
  // When both a label *and* a placeholder are set, float the label permanently
  // so the placeholder hint can sit in the input slot. Otherwise the label
  // would collide with the placeholder when the field is empty + unfocused.
  const isFloating =
    focused || valueLength > 0 || hasNativePlaceholder || (hasLabel && hasPlaceholder);
  // Show placeholder when no label (standard input behavior), when focused, or
  // when a placeholder is provided alongside a label (the label has floated up
  // to make room for it).
  const effectivePlaceholder = !hasLabel || focused || hasPlaceholder ? placeholder : undefined;
  const invalid = !!error;
  const showCounter = (counter ?? true) && typeof maxLength === "number";
  const atLimit = showCounter && valueLength >= maxLength;
  const internalDescribedBy = error ? errorId : helperText ? helperId : undefined;
  const describedBy = [internalDescribedBy, userDescribedBy].filter(Boolean).join(" ") || undefined;
  // Admin keeps the original rounded-2xl ring-style field; the public site keeps
  // the new MUI filled input (4px radius, 1px border, white fill, green focus).
  const isAdmin = useScopeClass() === "admin-scope";

  return (
    // text-left: the label span is pinned left-3/right-3 so it fills the field and its
    // text follows the ancestor's text-align; a text-center wrapper centred it.
    <div className={cn("space-y-1 text-left", className)}>
      <label
        htmlFor={inputId}
        className={cn(
          // mb-0/font-normal/text-foreground override the project-wide `label`
          // rule in index.css (which applies pl-2 mb-2 font-medium block
          // text-muted-foreground to every label).
          "relative mb-0 cursor-text font-normal text-foreground",
          isAdmin
            ? // Original: rounded-2xl, transparent fill, inset ring.
              [
                "rounded-2xl bg-transparent transition-shadow",
                "ring-[1.5px] ring-inset ring-input focus-within:ring-[1.5px] focus-within:ring-ring",
                invalid && "ring-destructive focus-within:ring-destructive",
              ]
            : // New MUI filled input: 4px radius, 1px #d5d4d2 border, white fill, green focus.
              [
                "rounded border border-input bg-white transition-colors focus-within:border-ring",
                invalid && "border-destructive focus-within:border-destructive",
              ],
          disabled && "cursor-not-allowed opacity-60",
          // With label: tall, label sits inside. Without: shorter, flex-centered.
          // Public site matches old app's 58px MUI TextField height.
          hasLabel
            ? isAdmin
              ? "block h-14 pt-[23px] pb-[9px]"
              : "block h-[58px] pt-[24px] pb-[9px]"
            : "flex h-11 items-center",
          // Horizontal padding shifts right when leading icon is present.
          leadingIcon ? "pl-9" : "pl-3",
          trailingIcon ? "pr-9" : "pr-3",
          showCounter && "pr-16",
          inputClassName,
        )}
      >
        {leadingIcon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          >
            {leadingIcon}
          </span>
        )}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={effectivePlaceholder}
          {...(value !== undefined ? { value } : { defaultValue })}
          onChange={(e) => {
            if (!controlled) setUncontrolledLength(e.target.value.length);
            onChange?.(e);
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          data-slot="floating-input"
          className="block w-full bg-transparent text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed"
        />
        {hasLabel && (
          <span
            className={cn(
              // One line, clipped: a label that wraps lands on top of the value.
              "pointer-events-none absolute origin-left truncate transition-all duration-150 ease-out",
              isAdmin ? "text-muted-foreground" : "text-input-label",
              leadingIcon ? "left-9" : "left-3",
              showCounter ? "right-16" : trailingIcon ? "right-9" : "right-3",
              // Floating size matches the old MUI shrunk label (0.9rem × 0.75 ≈ 10.8px).
              isFloating ? "top-[9px] text-[10.8px]" : "top-1/2 -translate-y-1/2 text-base",
              invalid && "text-destructive",
            )}
          >
            {label}
            {/* Legacy's fields were MUI TextFields, which append the asterisk
                themselves whenever `required` is set — so required markers were
                site-wide, not just on this form. `required` still reaches the
                input below via `rest` for native validation; this only mirrors it
                visually. aria-hidden because the input's own `required` is what
                screen readers announce. */}
            {rest.required && <span aria-hidden> *</span>}
          </span>
        )}
        {showCounter && (
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute right-3 text-xs tabular-nums",
              hasLabel ? "bottom-[9px]" : "top-1/2 -translate-y-1/2",
              atLimit ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {valueLength}/{maxLength}
          </span>
        )}
        {trailingIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {trailingIcon}
          </span>
        )}
      </label>
      {error ? (
        <p id={errorId} className="pl-3 text-xs text-destructive">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="pl-3 text-xs text-muted-foreground">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

export { FloatingInput };
export type { FloatingInputProps };
